/* eslint-disable max-lines */
import Button from '@components/atoms/ui-kit/button.tsx';
import { useMemo, useState } from 'react';
import { usePageHeader } from '@utils/hooks/use-page-header';
import { useTranslation } from 'react-i18next';
import CreateGlossaryModal from '@components/glossaries/createGlossaryModal';
import EditGlossaryModal from '@components/glossaries/editGlossaryModal';
import DeleteGlossaryModal from '@components/glossaries/deleteGlossaryModal';
import { Plus, Edit, Trash2, Download } from 'lucide-react';
import { useGetGlossaries } from '@utils/api/glossaries/use-get-glossaries';
import { type Resolver, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import type { GridAction } from '@components/atoms/datatable/grid-action-controller';
import {
  CreateGlossaryRequest,
  UpdateGlossaryRequest,
  Glossary
} from '@utils/types/glossary';
import { GET_URL } from '@utils/api/endpoints';
import { fmtDate } from '@utils/format';
import { Axios } from '@utils/api/axios-instance';
import toast from 'react-hot-toast';
import { Badge } from '@components/atoms/ui-kit/badge.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';

type Row = {
  id: number;
  name: string;
  source_language: string;
  target_language: string;
  language_pair: string;
  entry_count: number;
  is_active: boolean;
  created_at: string;
};

export default function Glossaries() {
  const { t } = useTranslation();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedGlossary, setSelectedGlossary] = useState<Glossary | null>(
    null
  );

  const { data, isLoading } = useGetGlossaries();

  const createSchema = Yup.object().shape({
    name: Yup.string()
      .required(t('glossary-name-is-required'))
      .max(100, t('glossary-name-max-length')),
    description: Yup.string().optional(),
    source_language: Yup.string().required(t('source-language-is-required')),
    target_language: Yup.string().required(t('target-language-is-required')),
    entries_csv: Yup.string().required(t('term-pairs-are-required'))
  });

  const updateSchema = Yup.object().shape({
    name: Yup.string().max(100, t('glossary-name-max-length')).optional(),
    description: Yup.string().optional(),
    is_active: Yup.boolean().optional()
  });

  const {
    control: createControl,
    handleSubmit: handleCreateSubmit,
    reset: resetCreate,
    register: registerCreate,
    setValue: setCreateValue,
    formState: { errors: createErrors }
  } = useForm<CreateGlossaryRequest>({
    resolver: yupResolver(createSchema),
    defaultValues: {
      name: '',
      description: '',
      source_language: '',
      target_language: '',
      entries_csv: ''
    }
  });

  const {
    control: editControl,
    handleSubmit: handleEditSubmit,
    reset: resetEdit,
    register: registerEdit,
    formState: { errors: editErrors }
  } = useForm<UpdateGlossaryRequest>({
    resolver: yupResolver(updateSchema) as Resolver<UpdateGlossaryRequest>
  });

  const rows = useMemo<Row[]>(() => {
    if (!data?.result) return [];
    return data.result.map((item: Glossary) => ({
      id: item.id,
      name: item.name,
      source_language: item.source_language,
      target_language: item.target_language,
      language_pair: `${item.source_language} → ${item.target_language}`,
      entry_count: item.entry_count,
      is_active: item.is_active,
      created_at: item.created_at
    }));
  }, [data]);

  const handleDownloadEntries = async (glossaryId: number, name: string) => {
    try {
      const url = GET_URL(glossaryId.toString()).glossaryEntries;
      if (!url) return;

      const { data: csvText } = await Axios.get<string>(url, {
        headers: { Accept: 'text/csv' }
      });

      const blob = new Blob([csvText], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${name.replace(/\s+/g, '_')}_glossary.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(t('glossary-entries-downloaded-successfully'));
    } catch {
      toast.error(t('failed-to-download-glossary-entries'));
    }
  };

  const handleEdit = (glossary: Glossary) => {
    setSelectedGlossary(glossary);
    resetEdit({
      name: glossary.name,
      description: glossary.description || '',
      is_active: glossary.is_active
    });
    setIsEditModalOpen(true);
  };

  const handleDelete = (glossary: Glossary) => {
    setSelectedGlossary(glossary);
    setIsDeleteModalOpen(true);
  };

  const actions = useMemo<GridAction<Row>[]>(() => {
    return [
      {
        id: 'edit',
        label: t('edit'),
        icon: <Edit className="h-4 w-4" />,
        run: row => {
          const glossary = data?.result.find(g => g.id === row.id);
          if (glossary) handleEdit(glossary);
        }
      },
      {
        id: 'download',
        label: t('download'),
        icon: <Download className="h-4 w-4" />,
        run: async row => {
          const glossary = data?.result.find(g => g.id === row.id);
          if (glossary) await handleDownloadEntries(glossary.id, glossary.name);
        }
      },
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-4 w-4" />,
        intent: 'danger',
        run: row => {
          const glossary = data?.result.find(g => g.id === row.id);
          if (glossary) handleDelete(glossary);
        }
      }
    ];
  }, [t, data]);

  const columns = useMemo<DataGridColumn<Row>[]>(() => {
    return [
      {
        id: 'name',
        header: t('name'),
        field: 'name',
        minWidth: 200,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'name',
        serverOperator: 'ilike',
        cell: ({ getValue }) => (
          <Text as="span" size="sm" weight="medium">
            {String(getValue() ?? '')}
          </Text>
        )
      },
      {
        id: 'language_pair',
        header: t('language-pair'),
        field: 'language_pair',
        minWidth: 150,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => (
          <Text as="span" size="sm" className="font-mono">
            {String(getValue() ?? '')}
          </Text>
        )
      },
      {
        id: 'entry_count',
        header: t('entries'),
        field: 'entry_count',
        maxWidth: 100,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => (
          <Text as="span" size="sm">
            {String(getValue() ?? '0')}
          </Text>
        )
      },
      {
        id: 'is_active',
        header: t('status'),
        field: 'is_active',
        maxWidth: 120,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'select',
        filterKey: 'is_active',
        serverOperator: 'eq',
        filterOptions: [
          { label: t('active'), value: 'true' },
          { label: t('inactive'), value: 'false' }
        ],
        cell: ({ getValue }) => {
          const active = Boolean(getValue());
          return (
            <Badge color={active ? 'green' : 'zinc'}>
              {active ? t('active') : t('inactive')}
            </Badge>
          );
        }
      },
      {
        id: 'created_at',
        header: t('created'),
        field: 'created_at',
        minWidth: 150,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => {
          const date = getValue();
          if (!date)
            return (
              <Text as="span" size="sm" tone="muted">
                —
              </Text>
            );
          try {
            return (
              <Text as="span" size="sm">
                {fmtDate(String(date))}
              </Text>
            );
          } catch {
            return (
              <Text as="span" size="sm">
                {String(date)}
              </Text>
            );
          }
        }
      }
    ];
  }, [t]);

  const showEmptyState = rows.length === 0 && !isLoading && !!data;

  usePageHeader({
    title: t('glossaries'),
    description: t('manage-translation-glossaries'),
    actions: (
      <Button
        onClick={() => {
          resetCreate({
            name: '',
            description: '',
            source_language: '',
            target_language: '',
            entries_csv: ''
          });
          setIsCreateModalOpen(true);
        }}
        label={t('create-glossary')}
        icon={<Plus width={14} />}
      />
    )
  });

  return (
    <div className="bg-app min-h-screen">
      <CreateGlossaryModal
        isModalOpen={isCreateModalOpen}
        setIsModalOpen={setIsCreateModalOpen}
        reset={resetCreate}
        handleSubmit={handleCreateSubmit}
        control={createControl}
        errors={createErrors}
        register={registerCreate}
        setValue={setCreateValue}
      />

      {selectedGlossary && (
        <>
          <EditGlossaryModal
            glossaryId={selectedGlossary.id}
            isModalOpen={isEditModalOpen}
            setIsModalOpen={setIsEditModalOpen}
            handleSubmit={handleEditSubmit}
            control={editControl}
            errors={editErrors}
            register={registerEdit}
          />

          <DeleteGlossaryModal
            glossary={selectedGlossary}
            isModalOpen={isDeleteModalOpen}
            setIsModalOpen={setIsDeleteModalOpen}
            onDeleteSuccess={() => setSelectedGlossary(null)}
          />
        </>
      )}

      <div className="pb-4 sm:pb-0 relative">
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          actions={actions}
          enableRowSelection={false}
          enablePagination={false}
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          showColumnFilters={false}
          filterMode="popover"
          height="88vh"
          dense
          grid={false}
          striped
          loading={isLoading}
          getRowId={row => String(row.id)}
        />

        {showEmptyState && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
            <div className="pointer-events-auto flex flex-col items-center gap-3 mt-8">
              <Text as="p" size="sm" tone="muted">
                {t('no-glossaries-yet')}
              </Text>
              <Button
                label={t('create-first-glossary')}
                icon={<Plus width={14} />}
                onClick={() => {
                  resetCreate({
                    name: '',
                    description: '',
                    source_language: '',
                    target_language: '',
                    entries_csv: ''
                  });
                  setIsCreateModalOpen(true);
                }}
                variant="primary"
                size="small"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
